import {
	topTilesSalesMetrics,
	topTilesOpdMetrics,
	replenishtInventoryMetrics,
	replenishmentInventoryMetricsWithDimension,
	replenishmentOPDMetrics,
	replenishmentOPDMetricsWithDimension,
	podInventoryMetricsWithDimension,
	podSalesMetricsWithDimension,
	pricingSalesMetricsWithDimension,
	DIMENSION,
} from './constants';
import { gql } from '@apollo/client';
import { apolloClientWithoutCache } from './apollo';
import { v4 as uuid4 } from 'uuid';
import { getInventoryQueryBuilder } from './queries/metrics/inventory-query';
import { getSalesQueryBuilder } from './queries/metrics/sales-query';
import { getOpdQueryBuilder } from './queries/metrics/opd-query';

export const buildRootCauseMetricsQuery = (
	rootCause,
	additionalMetrics,
	type
) => {
	if (rootCause === 'inventoryHealth') {
		return buildReplenishmentQuery(additionalMetrics, type);
	} else if (rootCause === 'pod') {
		return buildPodQuery(additionalMetrics, type);
	} else if (rootCause == 'pricing') {
		return buildPricingQuery(additionalMetrics, type);
	} else if (rootCause == 'assortment') {
		return buildAssortmentQuery(additionalMetrics, type);
	}
};

/* building replenishment query */
export const buildReplenishmentQuery = (additionalMetrics, type) => {
	const topTilesData = additionalMetrics
		? `topTilesData: ${buildTopTilesQuery(type)}`
		: '';
	const inventoryMetricsQuery = getInventoryQueryBuilder(
		replenishtInventoryMetrics,
		DIMENSION.WM_ITEM_NUMBER,
		true,
		true,
		['95']
	);
	const inventoryMetricsWithDimensionQuery = getInventoryQueryBuilder(
		replenishmentInventoryMetricsWithDimension,
		DIMENSION.WM_ITEM_NUMBER,
		false,
		true,
		['95']
	);
	const opdMetricsQuery = getOpdQueryBuilder(
		replenishmentOPDMetrics,
		DIMENSION.WM_ITEM_NUMBER,
		true
	);
	const opdMetricsWithDimensionQuery = getOpdQueryBuilder(
		replenishmentOPDMetricsWithDimension,
		DIMENSION.WM_ITEM_NUMBER,
		false
	);

	const query = `
        query federatedMetricValues($startDate: String!, $endDate: String!, $ids: [String], $channel: [String], $id: [String]) {
           ${topTilesData}
           inventoryWithoutDimension: ${inventoryMetricsQuery}
           inventoryWithDimension: ${inventoryMetricsWithDimensionQuery}
           opdWithoutDimension: ${opdMetricsQuery}
           opdMetricsWithDimension: ${opdMetricsWithDimensionQuery}
        }
    `;
	return query;
};

/* buiding the pod query */
export const buildPodQuery = (additionalMetrics, type) => {
	const topTilesData = additionalMetrics
		? `topTilesData: ${buildTopTilesQuery(type)}`
		: '';
	const salesWithDimensionQuery = getSalesQueryBuilder(
		podSalesMetricsWithDimension,
		DIMENSION.WM_ITEM_NUMBER
	);
	const inventoryWithDimensionQuery = getInventoryQueryBuilder(
		podInventoryMetricsWithDimension,
		DIMENSION.WM_ITEM_NUMBER,
		false,
		false,
		['95']
	);

	const query = `
        query federatedMetricValues($startDate: String!, $endDate: String!, $ids: [String], $channel: [String], $id: [String]) {
            ${topTilesData}
            salesWithDimension: ${salesWithDimensionQuery}
            inventoryWithDimension: ${inventoryWithDimensionQuery}
        }
    `;
	return query;
};

/* building the pricing query */
export const buildPricingQuery = (additionalMetrics, type) => {
	const topTilesData = additionalMetrics
		? `topTilesData: ${buildTopTilesQuery(type)}`
		: '';
	const salesWithDimensionQuery = getSalesQueryBuilder(
		pricingSalesMetricsWithDimension,
		DIMENSION.WM_ITEM_NUMBER
	);

	const query = `
        query federatedMetricValues($startDate: String!, $endDate: String!, $ids: [String], $channel: [String], $id: [String]) {
            ${topTilesData}
            salesWithDimension: ${salesWithDimensionQuery}
        }
    `;
	return query;
};

export const buildAssortmentQuery = (additionalMetrics, type) => {
	const topTilesData = additionalMetrics
		? `topTilesData: ${buildTopTilesQuery(type)}`
		: '';
	const query = `
        query federatedMetricValues($startDate: String!, $endDate: String!, $channel: [String], $id: [String]) {
            ${topTilesData}
        }
    `;
	return query;
};

/* building the top-tiles query */
export const buildTopTilesQuery = (type) => {
	const dimension = DIMENSION[type];

	return `
        federatedMetricValues(
            input: {
                attributeFilters: [
                    {
                        attribute: "DIMENSION"
                        name: "${dimension}"
                        values: $id
                    }
                    {
                        attribute: "DIMENSION"
                        name: "channel"
                        values: $channel
                    }
                ],
                timeFilters: [
                    {
                        startDate: $startDate
                        endDate: $endDate
                    }
                ]
            }
        ) {
            topTilesSalesData: sales {
                edges {
                    node {
                        dimensions { 
                            ${dimension} 
                        }
                        metrics {
                            ${topTilesSalesMetrics.join(`
                            `)}
                        }
                    }
                }
            }
            topTilesOpdData: opd {
                edges {
                    node {
                        dimensions { 
                            ${dimension} 
                        }
                        metrics {
                            ${topTilesOpdMetrics.join(`
                            `)}
                        }
                    }
                }
            }
        }
    `;
};

export const generateFederatedMetricsQuery = (
	rootCause,
	additionalMetrics,
	payload
) => {
	const queryStr = buildRootCauseMetricsQuery(
		rootCause,
		additionalMetrics,
		payload.type
	);
	const query = gql`
		${queryStr}
	`;
	return apolloClientWithoutCache.query({
		query,
		context: {
			headers: {
				'x-Apollo-operation-name': 'federatedMetricValues',
			},
		},
		variables: payload,
	});
};

export const retrieveAnomalies = (anomalies, attribute) => {
	const filteredAnomalies = anomalies.filter(
		(anomaly) => anomaly.node.attribute == attribute
	);
	return filteredAnomalies;
};

export const getAttributeIds = (anomalies = []) => {
	const ids = [];
	for (let i = 0; i < anomalies.length; i++) {
		ids.push(anomalies[i].node.id);
	}
	return ids;
};

export const retriveAttributeIds = (anomalies, attribute) => {
	const attributeAnomalies = retrieveAnomalies(anomalies.edges, attribute);
	const attributeIds = getAttributeIds(attributeAnomalies);
	return attributeIds;
};

export const concatAnomaliesWithSalesResponse = (
	anomaliesObj = [],
	salesMetricsResponse,
	dimension
) => {
	const edges = salesMetricsResponse?.edges;
	const anomalies = [...anomaliesObj];

	const anomaliesList = anomalies.map((anomaly) => {
		const temp = { ...anomaly };
		const nodeObj = edges.find(
			(edge) => edge.node.dimensions[dimension] === anomaly.node.id
		);
		if (nodeObj) {
			temp.node = {};
			Object.assign(temp.node, anomaly.node, nodeObj.node.metrics);
		}
		return temp;
	});
	return anomaliesList;
};

export const getAnomaliesList = (anomalies) => {
	return anomalies.map((edge) => {
		return {
			id: edge.node.id,
			name: edge.node.name,
			marketShare: edge.node.marketShare,
			marketShareChng: edge.node.marketShareChng,
			rcaTags: edge.node.rcaTags,
		};
	});
};

export const createAnomalyDetectionResponse = (
	anomalySummaryResponse = {},
	salesMetricsResponse = {},
	supplierShareResponse = {}
) => {
	let deptAnomalies = retrieveAnomalies(
		anomalySummaryResponse?.anomalies?.edges,
		'DEPARTMENT'
	);
	const catAnomalies = retrieveAnomalies(
		anomalySummaryResponse?.anomalies?.edges,
		'CATEGORY'
	);
	const subCatAnomalies = retrieveAnomalies(
		anomalySummaryResponse?.anomalies?.edges,
		'SUBCATEGORY'
	);
	deptAnomalies = concatAnomaliesWithSalesResponse(
		deptAnomalies,
		salesMetricsResponse?.federatedMetricValues?.sales,
		'omniDeptNbr'
	);

	const payload = {
		deptAnomalies: deptAnomalies,
		catAnomalies: catAnomalies,
		subCatAnomalies: subCatAnomalies,
		supplierShare: supplierShareResponse,
		departmentTableRows: deptAnomalies,
		departmentTotal: parseInt(anomalySummaryResponse.total.department),
		categoryTotal: parseInt(anomalySummaryResponse.total.category),
		subcategoryTotal: parseInt(anomalySummaryResponse.total.subcategory),
		departmentPositiveItems: parseInt(
			anomalySummaryResponse.positive.department
		),
		categoryPositiveItems: parseInt(anomalySummaryResponse.positive.category),
		subcategoryPositiveItems: parseInt(
			anomalySummaryResponse.positive.subcategory
		),
		departmentNegativeItems: parseInt(
			anomalySummaryResponse.negative.department
		),
		categoryNegativeItems: parseInt(anomalySummaryResponse.negative.category),
		subcategoryNegativeItems: parseInt(
			anomalySummaryResponse.negative.subcategory
		),
	};
	return payload;
};

export const retrieveWmItemNbrs = (rootCauseDetailsList = []) => {
	return rootCauseDetailsList.map((rootCauseDetail) => {
		return rootCauseDetail.node.wmItemNbr;
	});
};

const sortRootCausesArr = (rootCauses, anomalyChange) => {
	const rootCausesArr = rootCauses.filter(
		(rootCause) => rootCause.totalSalesImpact != null
	);

	rootCausesArr.sort((rootCause1, rootCause2) => {
		if (anomalyChange > 1) {
			return (
				parseFloat(rootCause2.totalSalesImpact) -
				parseFloat(rootCause1.totalSalesImpact)
			);
		} else {
			return (
				parseFloat(rootCause1.totalSalesImpact) -
				parseFloat(rootCause2.totalSalesImpact)
			);
		}
	});

	return rootCausesArr;
};

const extractDimensions = (edges) => {
	const updatedEdges = edges.map((edge) => {
		const { dimensions, ...rest } = edge.node;
		const { wmItemNbr, wmItemNm } = dimensions;
		const obj = {
			node: {
				...rest,
			},
		};
		obj.node['wmItemNbr'] = wmItemNbr;
		obj.node['wmItemNm'] = wmItemNm;
		return obj;
	});
	const temp = {
		edges: updatedEdges,
	};
	return temp;
};

export function transformRCAResponse(rootCauseResponse, anomalyChange) {
	const transResp = Object.keys(rootCauseResponse)
		.filter((domain) => domain !== '__typename')
		.map((domain) => {
			let newObj = { domain: domain, details: [] };

			for (let key in rootCauseResponse[domain]) {
				if (key === 'causes') {
					newObj.details = extractDimensions(
						rootCauseResponse[domain][key].edges
					);
				} else if (key !== '__typename') {
					newObj[key] = rootCauseResponse[domain][key];
				}
			}
			return newObj;
		});
	const updatedResponse = sortRootCausesArr(transResp, anomalyChange);
	return updatedResponse;
}

export const updateRtCauseRespwithDimMetrics = (
	response,
	metricsResponse,
	domain
) => {
	const rootCausesResponse = { ...response };
	const rootCausesArr = rootCausesResponse.rootCauses.map((rootCause) => {
		const temp = [];
		if (rootCause.domain === domain) {
			rootCause.details.edges.forEach((cause, i) => {
				const match = metricsResponse.find(
					(edge) => edge?.node.dimensions['wmItemNbr'] === cause?.node.wmItemNbr
				);
				const obj = {
					node: {
						...rootCause.details.edges[i].node,
						...match?.node.metrics,
					},
				};
				temp.push(obj);
			});
			const duplicateRootCauseObj = {
				details: {
					edges: [...temp],
				},
			};
			for (const key in rootCause) {
				if (key != 'details') {
					duplicateRootCauseObj[key] = rootCause[key];
				}
			}
			return duplicateRootCauseObj;
		} else {
			return rootCause;
		}
	});
	const updatedRootCauseObj = {};

	for (const key in rootCausesResponse) {
		if (key != 'rootCauses') {
			updatedRootCauseObj[key] = rootCausesResponse[key];
		} else {
			updatedRootCauseObj['rootCauses'] = rootCausesArr;
		}
	}
	return updatedRootCauseObj;
};

export const updateRtCauseRespWithMetrics = (
	rootCausesResponse,
	metricsResponse,
	domain
) => {
	rootCausesResponse.rootCauses.forEach((rootCause, index) => {
		if (rootCause.domain === domain) {
			rootCausesResponse.rootCauses[index] = {
				...rootCause,
				...metricsResponse[0].node.metrics,
			};
		}
	});
	return rootCausesResponse;
};

export const updateTopTilesData = (rootCausesResponse, metricsResponse) => {
	rootCausesResponse = {
		...rootCausesResponse,
		...metricsResponse[0].node.metrics,
	};
	return rootCausesResponse;
};

export const concatRootCauseWithMetricsResponse = (
	rootCause,
	additionalMetrics,
	rootCausesResponse,
	federatedMetricsResponse
) => {
	if (rootCause === 'inventoryHealth') {
		if (additionalMetrics) {
			rootCausesResponse = updateTopTilesData(
				rootCausesResponse,
				federatedMetricsResponse.topTilesData.topTilesSalesData?.edges
			);
			rootCausesResponse = updateTopTilesData(
				rootCausesResponse,
				federatedMetricsResponse.topTilesData.topTilesOpdData?.edges
			);
		}
		rootCausesResponse = updateRtCauseRespWithMetrics(
			rootCausesResponse,
			federatedMetricsResponse.inventoryWithoutDimension?.inventory?.edges,
			rootCause
		);
		rootCausesResponse = updateRtCauseRespWithMetrics(
			rootCausesResponse,
			federatedMetricsResponse.opdWithoutDimension?.opd?.edges,
			rootCause
		);
		rootCausesResponse = updateRtCauseRespwithDimMetrics(
			rootCausesResponse,
			federatedMetricsResponse.inventoryWithDimension?.inventory?.edges,
			rootCause
		);
		rootCausesResponse = updateRtCauseRespwithDimMetrics(
			rootCausesResponse,
			federatedMetricsResponse.opdMetricsWithDimension?.opd?.edges,
			rootCause
		);
		return rootCausesResponse;
	} else if (rootCause === 'pod') {
		if (additionalMetrics) {
			rootCausesResponse = updateTopTilesData(
				rootCausesResponse,
				federatedMetricsResponse.topTilesData.topTilesSalesData?.edges
			);
			rootCausesResponse = updateTopTilesData(
				rootCausesResponse,
				federatedMetricsResponse.topTilesData.topTilesOpdData?.edges
			);
		}
		rootCausesResponse = updateRtCauseRespwithDimMetrics(
			rootCausesResponse,
			federatedMetricsResponse.inventoryWithDimension?.inventory?.edges,
			rootCause
		);
		rootCausesResponse = updateRtCauseRespwithDimMetrics(
			rootCausesResponse,
			federatedMetricsResponse.salesWithDimension?.sales?.edges,
			rootCause
		);
		return rootCausesResponse;
	} else if (rootCause === 'pricing') {
		if (additionalMetrics) {
			rootCausesResponse = updateTopTilesData(
				rootCausesResponse,
				federatedMetricsResponse.topTilesData.topTilesSalesData?.edges
			);
			rootCausesResponse = updateTopTilesData(
				rootCausesResponse,
				federatedMetricsResponse.topTilesData.topTilesOpdData?.edges
			);
		}
		rootCausesResponse = updateRtCauseRespwithDimMetrics(
			rootCausesResponse,
			federatedMetricsResponse.salesWithDimension?.sales?.edges,
			rootCause
		);
		return rootCausesResponse;
	} else if (rootCause === 'assortment') {
		if (additionalMetrics) {
			rootCausesResponse = updateTopTilesData(
				rootCausesResponse,
				federatedMetricsResponse.topTilesData.topTilesSalesData?.edges
			);
			rootCausesResponse = updateTopTilesData(
				rootCausesResponse,
				federatedMetricsResponse.topTilesData.topTilesOpdData?.edges
			);
		}
		return rootCausesResponse;
	}
};

export const getWmtItemNrbs = (rootCauseArr) => {
	const arrEdges = rootCauseArr[0].details.edges;
	const arr = arrEdges.map((obj) => obj.node.wmItemNbr);
	return arr;
};

export const setWmItemNbrs = (rootCausesList) => {
	return rootCausesList.map((rootCause) => {
		return rootCause.node.dimensions.wmItemNbr;
	});
};

export const clientRequestID = () => uuid4();

const mergeArrays = (arr1, arr2, key) => {
	let mergedArray = [];

	// Create a map for quick lookup by key
	let map = new Map();

	arr1.forEach((item) => map.set(item[key], { ...item }));
	arr2.forEach((item) => {
		if (map.has(item[key])) {
			map.set(item[key], { ...map.get(item[key]), ...item });
		} else {
			map.set(item[key], { ...item });
		}
	});

	// Convert map values to array
	mergedArray = Array.from(map.values());
	return mergedArray;
};

export const appendRootCauseDetailsWithDimension = (response) => {
	let arr = [];
	response.forEach((edge) => {
		const obj = {};
		for (let item in edge.node.dimensions) {
			if (item !== '__typename') {
				obj[item] = edge.node.dimensions[item];
			}
		}
		for (let item in edge.node.metrics) {
			if (item !== '__typename') {
				obj[item] = edge.node.metrics[item];
			}
		}
		arr.push(obj);
	});
	return arr;
};

export const appendRootCauseDetails = (obj, response) => {
	const temp = {
		...obj,
		...response[0].node.metrics,
	};
	return temp;
};

export const updateRootCauseDetails = (federatedMetricsResponse, rootCause) => {
	if (rootCause === 'inventoryHealth') {
		let obj = {};
		// Without Dimension
		obj = appendRootCauseDetails(
			obj,
			federatedMetricsResponse.data.inventoryWithoutDimension.inventory.edges
		);
		obj = appendRootCauseDetails(
			obj,
			federatedMetricsResponse.data.opdWithoutDimension.opd.edges
		);
		// With Dimension
		const response3 = appendRootCauseDetailsWithDimension(
			federatedMetricsResponse.data.opdMetricsWithDimension.opd.edges
		);
		const response4 = appendRootCauseDetailsWithDimension(
			federatedMetricsResponse.data.inventoryWithDimension.inventory.edges
		);
		const mergeResponse = mergeArrays(response3, response4, 'wmItemNbr');
		const node = {
			details: mergeResponse,
			rootCause: 'inventoryHealth',
			...obj,
		};
		return node;
	} else if (rootCause === 'pod') {
		// With Dimension
		const response1 = appendRootCauseDetailsWithDimension(
			federatedMetricsResponse.data.inventoryWithDimension.inventory.edges
		);
		const response2 = appendRootCauseDetailsWithDimension(
			federatedMetricsResponse.data.salesWithDimension.sales.edges
		);
		const mergeResponse = mergeArrays(response1, response2, 'wmItemNbr');

		const node = {
			details: mergeResponse,
			rootCause: 'pod',
		};
		return node;
	} else if (rootCause === 'pricing') {
		//  With Dimension
		const response = appendRootCauseDetailsWithDimension(
			federatedMetricsResponse.data.salesWithDimension.sales.edges
		);
		const node = {
			details: response,
			rootCause: 'pricing',
		};
		return node;
	}
};

export const concatInventoryRootCauseWithMetrics = (
	federatedMetricsResponse,
	rootCauseResp
) => {
	const finalResponse = concatRootCauseWithMetricsResponse(
		'inventoryHealth',
		false,
		rootCauseResp,
		federatedMetricsResponse
	);
	return finalResponse;
};
